import { CheckCircleFilled, EditOutlined, LogoutOutlined, RocketOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Result, Space, Spin, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { hasActivationContinuation, hasPendingOnboarding, hasRecoverableAdminOnboarding, readPendingOnboarding } from 'services/auth/onboardingStatus';
import useAuthRepo from 'services/repo/useAuthRepo';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import { getTenantMemberships, provisionTemporaryWorkspace, saveSelectedTenant } from 'services/tenant/tenantMembershipService';
import LocalStorageUtils from 'utils/LocalStorage';

const PLAN_KEY = 'workplansiq.selectedPlan';
const ACCOUNT_KEY = 'workplansiq.accountInformation';
const readSession = (key) => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } };

export default function SubscriptionWelcomePage() {
  const navigate = useNavigate();
  const { isLoading, logout } = useAuthRepo(false);
  const [accessState, setAccessState] = useState('checking');
  const isAuthenticated = Boolean(LocalStorageUtils.jwtToken.get());
  const user = LocalStorageUtils.userInfo.get() || {};
  const plan = readSession(PLAN_KEY);
  const account = readSession(ACCOUNT_KEY);
  const activation = readPendingOnboarding();
  const signedInEmail = sessionStorage.getItem('workplansiq.signedInEmail');
  const email = activation?.email
    || account?.adminEmail
    || signedInEmail
    || user.email
    || user.emailAddress
    || user.preferred_username
    || user.username
    || 'Administrator account';
  const planTitle = plan?.title || activation?.planTitle || activation?.planCode || 'Plan not selected';
  const capacity = plan?.capacity || activation?.capacity;
  const durationMonths = plan?.durationMonths || activation?.durationMonths;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (!isAuthenticated) return;

    const verifyAccess = async () => {
      if (hasPendingOnboarding(email)
        || hasActivationContinuation(email)
        || hasRecoverableAdminOnboarding(email)) {
        setAccessState('allowed');
        return;
      }

      const memberships = await getTenantMemberships();
      if (memberships.length) {
        if (memberships.length === 1) {
          saveSelectedTenant(memberships[0]);
          navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`, { replace: true });
        } else {
          navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}`, { replace: true });
        }
        return;
      }

      setAccessState('missing-onboarding');
    };

    verifyAccess();
  }, []);

  if (!isAuthenticated) return <Navigate to={`/?redirectTo=${encodeURIComponent('/welcome')}`} replace />;
  if (accessState === 'missing-onboarding') {
    return <Navigate to={`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}?mode=manage`} replace />;
  }

  const continueToApplication = () => {
    provisionTemporaryWorkspace({ ownerEmail: email, selectedPlan: plan });
    navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`, { replace: true });
  };

  return (
    <main className="onboarding-page">
      <div className="onboarding-shell">
        <header className="onboarding-header">
          <div>
            <Typography.Text className="onboarding-eyebrow">WORKPLANSIQ</Typography.Text>
            <Typography.Title level={2}>Welcome to WorkPlansIQ</Typography.Title>
            <Typography.Paragraph type="secondary">Your administrator account is ready. You can enter WorkPlansIQ now and complete billing later.</Typography.Paragraph>
          </div>
          <Button danger icon={<LogoutOutlined />} loading={isLoading} onClick={logout}>Logout</Button>
        </header>

        <Card className="onboarding-card">
          {accessState === 'checking' ? (
            <section className="onboarding-activation">
              <Spin size="large" />
              <Typography.Title level={4}>Preparing your account...</Typography.Title>
            </section>
          ) : <section className="onboarding-activation">
            <Result
              status="success"
              icon={<CheckCircleFilled />}
              title="Your administrator account is ready"
              subTitle="Your email has been verified and your selected plan has been retained."
            />

            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label="Administrator">
                <Space wrap><strong>{email}</strong><Tag color="green">Verified</Tag></Space>
              </Descriptions.Item>
              <Descriptions.Item label="Selected plan"><strong>{planTitle}</strong></Descriptions.Item>
              {capacity ? <Descriptions.Item label="Capacity">{capacity} units</Descriptions.Item> : null}
              {durationMonths ? <Descriptions.Item label="Duration">{durationMonths} months</Descriptions.Item> : null}
              <Descriptions.Item label="Subscription status"><Tag color="gold">Setup incomplete</Tag></Descriptions.Item>
            </Descriptions>

            <Space wrap>
              <Button type="primary" size="large" icon={<RocketOutlined />} onClick={continueToApplication}>Continue to WorkPlansIQ</Button>
              <Button size="large" icon={<EditOutlined />} onClick={() => navigate('/onboarding?mode=change-plan')}>Change plan</Button>
            </Space>
          </section>}
        </Card>
      </div>
    </main>
  );
}
